// Measures how far an element sits from the top of the document
// (docs/06-ux-rules.md "Height model" / P10 D2). `HostFlexShell` uses the
// result to fill from wherever it mounts down to the bottom of the window
// (`calc(100vh - offset)`), replacing the prototype's hard-coded
// `calc(100vh - 452px)` magic number with a measured value that works
// under either host's page chrome.
import { RefObject, useEffect, useState } from 'react'

export function useViewportTopOffset(ref: RefObject<HTMLElement | null>) {
  const [offset, setOffset] = useState(0)

  useEffect(() => {
    const element = ref.current

    if (!element) {
      return
    }

    const measure = () => {
      // `getBoundingClientRect().top` alone is viewport-relative and
      // shrinks toward 0 as the page scrolls, which would make the shell
      // keep growing on every scroll. Adding `window.scrollY` converts it
      // back to a document-relative offset that only changes when the
      // chrome above the shell actually reflows.
      const { top } = element.getBoundingClientRect()
      setOffset(top + window.scrollY)
    }

    measure()

    window.addEventListener('resize', measure)

    if (typeof ResizeObserver === 'undefined') {
      return () => window.removeEventListener('resize', measure)
    }

    // Watches every ancestor up to (and including) document.body, not just
    // body itself — this app's shells are height-locked to the viewport
    // (the full-height class chain), so body's own box never resizes even
    // when the chrome above this ref does (e.g. a list's loading skeleton
    // collapsing into its real, differently-sized content once data
    // arrives). Observing only body missed exactly that case: the initial
    // measure() ran against the pre-data layout, body's size never changed
    // to trigger a re-measure, and the shell was left permanently bound to
    // a stale, too-large offset (confirmed live: forcing a resize event
    // corrected it instantly). Observing each ancestor catches a resize
    // wherever it actually happens. Still can't loop: a taller shell
    // doesn't move its own top, so the re-measure sets the same offset and
    // React bails out of the state update.
    const observer = new ResizeObserver(measure)
    for (
      let node: HTMLElement | null = element.parentElement;
      node;
      node = node.parentElement
    ) {
      observer.observe(node)
    }
    observer.observe(document.body)

    return () => {
      window.removeEventListener('resize', measure)
      observer.disconnect()
    }
  }, [ref])

  return offset
}
